import React, { useEffect, useState } from 'react';
import { Select } from 'components';
import { useLazyQuery } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import { IProgram } from '@types';
import { SCHOOLS } from 'graphql/queries/school';
import { DEFAULT } from 'shared/enums';
import { ProgramItem } from 'shared/components';
import { ProgramItemListStyled } from 'pages/users/invitation/flow/select-programs.styles';
import { generateSchools } from 'shared/components/program-cycle-selector/helpers.js';
import { useTranslation } from 'react-i18next';
import { Alert } from '@takecasper/acuity-design-system';

interface IProgramMultiSelect {
  selectedPrograms: IProgram[];
  setSelectedPrograms: (selectedPrograms: IProgram[]) => void;
  excludedSchoolIds?: string[];
  id?: string;
  className?: string;
  autoFocus?: boolean;
  styleOverride?: Object;
}

const ProgramMultiSelect = ({
  selectedPrograms,
  setSelectedPrograms,
  excludedSchoolIds = [],
  id = DEFAULT,
  className = '',
  autoFocus = true,
  styleOverride
}: IProgramMultiSelect) => {
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [schools, setSchools] = useState<IProgram[]>([]);

  const [
    schoolQuery,
    { loading, data: { schools: { total: maxPages = 0 } = {} } = {}, error }
  ] = useLazyQuery(SCHOOLS, {
    onCompleted: data => {
      setSchools([...generateSchools({ schools: data.schools.docs || [] })]);
    }
  });

  const getSchools = () => {
    return schoolQuery({
      variables: {
        page,
        limit: 20,
        search,
        excludeIds: [
          ...selectedPrograms?.map(sc => sc.value),
          ...excludedSchoolIds
        ]
      }
    });
  };

  /**
   * Refetch data based on the search value.
   */
  useEffect(() => {
    getSchools();
  }, [search]);

  /**
   * Refetch data when the page changes.
   */
  useEffect(() => {
    getSchools().then(data => {
      setSchools([
        ...schools,
        ...generateSchools({ schools: data?.data?.schools?.docs || [] })
      ]);
    });
  }, [page]);

  /**
   * Increment the page from which we will fetch more data, if we are at the end of the list.
   * @param e
   */
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const { offsetHeight, scrollTop, scrollHeight } = target;
    const scrollPosition = Math.ceil(offsetHeight + scrollTop);
    if (
      scrollPosition >= Math.floor(scrollHeight) &&
      !loading &&
      maxPages > page
    ) {
      setPage(page + 1);
    }
  };

  /**
   * Adds a program to the selected programs list.
   * @param selectedProgram
   */
  const addProgram = (selectedProgram: IProgram) => {
    setSelectedPrograms([...selectedPrograms, selectedProgram]);
  };

  /**
   * Removes a program from the selected programs list by ID.
   * @param schoolId
   */
  const removeProgram = (schoolId: string) => {
    setSelectedPrograms(selectedPrograms.filter(sc => sc.value !== schoolId));
  };

  /**
   * On input change, set the search value and reset the page to 1.
   * @param searchInput
   */
  const handleInputChange = (searchInput: string) => {
    setPage(1);
    setSearch(searchInput);
  };

  /**
   * If there is an error loading the schools, log the error and show an alert.
   */
  if (error) {
    datadogLogs.logger.error(
      'Insights program-multi-select component: Error fetching schools',
      {
        page: page,
        limit: 20,
        search,
        excludeIds: [
          ...selectedPrograms?.map(sc => sc.value),
          ...excludedSchoolIds
        ]
      },
      error
    );
    return (
      <Alert
        id={`${id}-program-multi-select-error`}
        data-testid={`${id}-program-multi-select-error`}
        data-pendoid={`${id}-program-multi-select-error`}
        variant="error"
        title={t?.('common_something_went_wrong')}
        description={t?.('common_please_try_again')}
        isDismissible={false}
      />
    );
  }

  return (
    <div
      data-testid={`${id}-program-multi-select`}
      data-pendoid={`${id}-program-multi-select`}
    >
      <Select
        styleOverride={styleOverride}
        className={className}
        data-testid={`${id}-program-multi-select-input`}
        data-pendoid={`${id}-program-multi-select-input`}
        label={t?.('select_program_filter_plural')}
        name={t?.('select_program_filter')}
        placeholder={t?.('select_program_search')}
        selectedOption={null} // This is intentionally null, since we track selected programs separately
        options={schools}
        onMenuOpen={getSchools}
        onMenuClose={() => {
          setSchools([]);
        }}
        handleChange={(option: IProgram) => addProgram(option)}
        handleInputChange={(searchInput: string) =>
          handleInputChange(searchInput)
        }
        handleScroll={handleScroll}
        autoFocus={autoFocus}
      />
      <ProgramItemListStyled
        data-testid={`${id}-program-multi-select-selected-programs`}
        data-pendoid={`${id}-program-multi-select-selected-programs`}
      >
        {selectedPrograms?.map(program => (
          <ProgramItem
            key={program.value}
            value={program.value}
            label={program.label}
            handleClick={removeProgram}
          />
        ))}
      </ProgramItemListStyled>
    </div>
  );
};

export default ProgramMultiSelect;
